/**
 * Permission codes, mirroring `com.schoolmanagement.common.security.Permissions`.
 *
 * These drive what the UI offers — which navigation entries render, which
 * buttons appear. That is a usability measure only: the backend checks the same
 * permission on every request and is the authority. Hiding a button the user
 * cannot use is courtesy; it is not access control, and nothing here should ever
 * be treated as such.
 */
export const PERMISSIONS = {
  SCHOOL_VIEW: 'SCHOOL_VIEW',
  SCHOOL_CREATE: 'SCHOOL_CREATE',
  SCHOOL_UPDATE: 'SCHOOL_UPDATE',
  SCHOOL_DELETE: 'SCHOOL_DELETE',

  CAMPUS_VIEW: 'CAMPUS_VIEW',
  CAMPUS_CREATE: 'CAMPUS_CREATE',
  CAMPUS_UPDATE: 'CAMPUS_UPDATE',
  CAMPUS_DELETE: 'CAMPUS_DELETE',

  ACADEMIC_YEAR_VIEW: 'ACADEMIC_YEAR_VIEW',
  ACADEMIC_YEAR_CREATE: 'ACADEMIC_YEAR_CREATE',
  ACADEMIC_YEAR_UPDATE: 'ACADEMIC_YEAR_UPDATE',
  ACADEMIC_YEAR_DELETE: 'ACADEMIC_YEAR_DELETE',
  ACADEMIC_YEAR_SET_CURRENT: 'ACADEMIC_YEAR_SET_CURRENT',

  GRADE_VIEW: 'GRADE_VIEW',
  GRADE_CREATE: 'GRADE_CREATE',
  GRADE_UPDATE: 'GRADE_UPDATE',
  GRADE_DELETE: 'GRADE_DELETE',

  GRADE_OFFERING_VIEW: 'GRADE_OFFERING_VIEW',
  GRADE_OFFERING_CREATE: 'GRADE_OFFERING_CREATE',
  GRADE_OFFERING_UPDATE: 'GRADE_OFFERING_UPDATE',
  GRADE_OFFERING_DELETE: 'GRADE_OFFERING_DELETE',

  SECTION_VIEW: 'SECTION_VIEW',
  SECTION_CREATE: 'SECTION_CREATE',
  SECTION_UPDATE: 'SECTION_UPDATE',
  SECTION_DELETE: 'SECTION_DELETE',

  SUBJECT_VIEW: 'SUBJECT_VIEW',
  SUBJECT_CREATE: 'SUBJECT_CREATE',
  SUBJECT_UPDATE: 'SUBJECT_UPDATE',
  SUBJECT_DELETE: 'SUBJECT_DELETE',

  STUDENT_VIEW: 'STUDENT_VIEW',
  STUDENT_CREATE: 'STUDENT_CREATE',
  STUDENT_UPDATE: 'STUDENT_UPDATE',
  STUDENT_DELETE: 'STUDENT_DELETE',

  ENROLLMENT_VIEW: 'ENROLLMENT_VIEW',
  ENROLLMENT_CREATE: 'ENROLLMENT_CREATE',
  ENROLLMENT_UPDATE: 'ENROLLMENT_UPDATE',
  ENROLLMENT_DELETE: 'ENROLLMENT_DELETE',

  USER_VIEW: 'USER_VIEW',
  USER_CREATE: 'USER_CREATE',
  USER_UPDATE: 'USER_UPDATE',
  USER_DELETE: 'USER_DELETE',

  ROLE_VIEW: 'ROLE_VIEW',
  ROLE_CREATE: 'ROLE_CREATE',
  ROLE_UPDATE: 'ROLE_UPDATE',
  ROLE_DELETE: 'ROLE_DELETE',

  PERMISSION_VIEW: 'PERMISSION_VIEW',
  AUDIT_VIEW: 'AUDIT_VIEW',

  // --- Student lifecycle -------------------------------------------------

  STUDENT_STATUS_UPDATE: 'STUDENT_STATUS_UPDATE',

  GUARDIAN_VIEW: 'GUARDIAN_VIEW',
  GUARDIAN_CREATE: 'GUARDIAN_CREATE',
  GUARDIAN_UPDATE: 'GUARDIAN_UPDATE',
  /** Linking a guardian to a child, as distinct from editing the person. */
  GUARDIAN_ASSIGN: 'GUARDIAN_ASSIGN',

  STUDENT_DOCUMENT_VIEW: 'STUDENT_DOCUMENT_VIEW',
  STUDENT_DOCUMENT_UPLOAD: 'STUDENT_DOCUMENT_UPLOAD',
  STUDENT_DOCUMENT_VERIFY: 'STUDENT_DOCUMENT_VERIFY',
  STUDENT_DOCUMENT_DELETE: 'STUDENT_DOCUMENT_DELETE',

  ENROLLMENT_PROMOTE: 'ENROLLMENT_PROMOTE',
  ENROLLMENT_TRANSFER: 'ENROLLMENT_TRANSFER',

  // --- Academic management -----------------------------------------------

  EMPLOYEE_VIEW: 'EMPLOYEE_VIEW',
  EMPLOYEE_CREATE: 'EMPLOYEE_CREATE',
  EMPLOYEE_UPDATE: 'EMPLOYEE_UPDATE',

  TEACHER_VIEW: 'TEACHER_VIEW',
  TEACHER_CREATE: 'TEACHER_CREATE',
  TEACHER_UPDATE: 'TEACHER_UPDATE',
  TEACHER_ASSIGN: 'TEACHER_ASSIGN',

  ACADEMIC_SUBJECT_VIEW: 'ACADEMIC_SUBJECT_VIEW',
  ACADEMIC_SUBJECT_CREATE: 'ACADEMIC_SUBJECT_CREATE',
  ACADEMIC_SUBJECT_UPDATE: 'ACADEMIC_SUBJECT_UPDATE',

  PERIOD_VIEW: 'PERIOD_VIEW',
  PERIOD_MANAGE: 'PERIOD_MANAGE',

  TIMETABLE_VIEW: 'TIMETABLE_VIEW',
  TIMETABLE_CREATE: 'TIMETABLE_CREATE',
  TIMETABLE_UPDATE: 'TIMETABLE_UPDATE',
  TIMETABLE_DELETE: 'TIMETABLE_DELETE',

  STUDENT_ATTENDANCE_VIEW: 'STUDENT_ATTENDANCE_VIEW',
  STUDENT_ATTENDANCE_CREATE: 'STUDENT_ATTENDANCE_CREATE',
  STUDENT_ATTENDANCE_UPDATE: 'STUDENT_ATTENDANCE_UPDATE',

  EMPLOYEE_ATTENDANCE_VIEW: 'EMPLOYEE_ATTENDANCE_VIEW',
  EMPLOYEE_ATTENDANCE_RECORD: 'EMPLOYEE_ATTENDANCE_RECORD',

  ASSIGNMENT_VIEW: 'ASSIGNMENT_VIEW',
  ASSIGNMENT_CREATE: 'ASSIGNMENT_CREATE',
  ASSIGNMENT_UPDATE: 'ASSIGNMENT_UPDATE',
  ASSIGNMENT_PUBLISH: 'ASSIGNMENT_PUBLISH',
  ASSIGNMENT_CLOSE: 'ASSIGNMENT_CLOSE',
  ASSIGNMENT_SUBMISSION_VIEW: 'ASSIGNMENT_SUBMISSION_VIEW',
  ASSIGNMENT_SUBMISSION_CREATE: 'ASSIGNMENT_SUBMISSION_CREATE',
  ASSIGNMENT_SUBMISSION_REVIEW: 'ASSIGNMENT_SUBMISSION_REVIEW',

  CALENDAR_VIEW: 'CALENDAR_VIEW',
  CALENDAR_MANAGE: 'CALENDAR_MANAGE',

  // --- Examination & assessment ------------------------------------------

  EXAMINATION_VIEW: 'EXAMINATION_VIEW',
  EXAMINATION_CREATE: 'EXAMINATION_CREATE',
  EXAMINATION_UPDATE: 'EXAMINATION_UPDATE',
  /** Moving an examination through its lifecycle, including archiving. */
  EXAMINATION_STATUS: 'EXAMINATION_STATUS',

  EXAM_SCHEDULE_VIEW: 'EXAM_SCHEDULE_VIEW',
  EXAM_SCHEDULE_MANAGE: 'EXAM_SCHEDULE_MANAGE',

  ASSESSMENT_VIEW: 'ASSESSMENT_VIEW',
  ASSESSMENT_MANAGE: 'ASSESSMENT_MANAGE',

  MARKS_VIEW: 'MARKS_VIEW',
  MARKS_ENTER: 'MARKS_ENTER',
  MARKS_SUBMIT: 'MARKS_SUBMIT',
  /** Separate from entering: a reviewer must not be the person who entered. */
  MARKS_REVIEW: 'MARKS_REVIEW',

  RESULT_VIEW: 'RESULT_VIEW',
  RESULT_CALCULATE: 'RESULT_CALCULATE',
  RESULT_PUBLISH: 'RESULT_PUBLISH',
  /** Changing a result after publication — always audited. */
  RESULT_AMEND: 'RESULT_AMEND',

  GRADING_SCALE_VIEW: 'GRADING_SCALE_VIEW',
  GRADING_SCALE_MANAGE: 'GRADING_SCALE_MANAGE',

  // Fees, billing and payments. Approve is separate from manage throughout:
  // proposing relief and granting it are different acts.
  DISCOUNT_APPROVE: 'DISCOUNT_APPROVE',
  DISCOUNT_MANAGE: 'DISCOUNT_MANAGE',
  DISCOUNT_VIEW: 'DISCOUNT_VIEW',

  FEE_ASSIGNMENT_MANAGE: 'FEE_ASSIGNMENT_MANAGE',
  FEE_ASSIGNMENT_VIEW: 'FEE_ASSIGNMENT_VIEW',

  FEE_CATEGORY_MANAGE: 'FEE_CATEGORY_MANAGE',
  FEE_CATEGORY_VIEW: 'FEE_CATEGORY_VIEW',

  FEE_STRUCTURE_MANAGE: 'FEE_STRUCTURE_MANAGE',
  FEE_STRUCTURE_VIEW: 'FEE_STRUCTURE_VIEW',

  FINANCE_REPORT_EXPORT: 'FINANCE_REPORT_EXPORT',
  FINANCE_REPORT_VIEW: 'FINANCE_REPORT_VIEW',

  FINANCE_SETTINGS_MANAGE: 'FINANCE_SETTINGS_MANAGE',
  FINANCE_SETTINGS_VIEW: 'FINANCE_SETTINGS_VIEW',

  INVOICE_CANCEL: 'INVOICE_CANCEL',
  INVOICE_CREATE: 'INVOICE_CREATE',
  INVOICE_ISSUE: 'INVOICE_ISSUE',
  INVOICE_UPDATE: 'INVOICE_UPDATE',
  INVOICE_VIEW: 'INVOICE_VIEW',

  LATE_FEE_APPLY: 'LATE_FEE_APPLY',
  LATE_FEE_MANAGE: 'LATE_FEE_MANAGE',
  LATE_FEE_VIEW: 'LATE_FEE_VIEW',

  PAYMENT_ALLOCATE: 'PAYMENT_ALLOCATE',
  PAYMENT_RECORD: 'PAYMENT_RECORD',
  PAYMENT_VIEW: 'PAYMENT_VIEW',
  PAYMENT_VOID: 'PAYMENT_VOID',

  REFUND_APPROVE: 'REFUND_APPROVE',
  REFUND_PROCESS: 'REFUND_PROCESS',
  REFUND_REQUEST: 'REFUND_REQUEST',
  REFUND_VIEW: 'REFUND_VIEW',

  SCHOLARSHIP_APPROVE: 'SCHOLARSHIP_APPROVE',
  SCHOLARSHIP_MANAGE: 'SCHOLARSHIP_MANAGE',
  SCHOLARSHIP_VIEW: 'SCHOLARSHIP_VIEW',

  STUDENT_LEDGER_VIEW: 'STUDENT_LEDGER_VIEW',

  // HR, staff and payroll. EMPLOYEE_SELF_* are scoped by the backend to the
  // holder's own record: a view of oneself, not a view.
  CONTRACT_MANAGE: 'CONTRACT_MANAGE',
  CONTRACT_TERMINATE: 'CONTRACT_TERMINATE',
  CONTRACT_VIEW: 'CONTRACT_VIEW',

  DEPARTMENT_MANAGE: 'DEPARTMENT_MANAGE',
  DEPARTMENT_VIEW: 'DEPARTMENT_VIEW',

  EMPLOYEE_DELETE: 'EMPLOYEE_DELETE',

  EMPLOYEE_SENSITIVE_VIEW: 'EMPLOYEE_SENSITIVE_VIEW',

  EMPLOYEE_STATUS: 'EMPLOYEE_STATUS',

  EMPLOYEE_DOCUMENT_MANAGE: 'EMPLOYEE_DOCUMENT_MANAGE',
  EMPLOYEE_DOCUMENT_VIEW: 'EMPLOYEE_DOCUMENT_VIEW',

  EMPLOYEE_SELF_LEAVE: 'EMPLOYEE_SELF_LEAVE',
  EMPLOYEE_SELF_PAYSLIP: 'EMPLOYEE_SELF_PAYSLIP',
  EMPLOYEE_SELF_VIEW: 'EMPLOYEE_SELF_VIEW',

  POSITION_MANAGE: 'POSITION_MANAGE',
  POSITION_VIEW: 'POSITION_VIEW',

  // ---- leave --------------------------------------------------------------
  LEAVE_TYPE_VIEW: 'LEAVE_TYPE_VIEW',
  LEAVE_TYPE_MANAGE: 'LEAVE_TYPE_MANAGE',

  LEAVE_REQUEST_VIEW: 'LEAVE_REQUEST_VIEW',
  LEAVE_REQUEST_CREATE: 'LEAVE_REQUEST_CREATE',
  LEAVE_REQUEST_APPROVE: 'LEAVE_REQUEST_APPROVE',
  LEAVE_REQUEST_HR_APPROVE: 'LEAVE_REQUEST_HR_APPROVE',
  LEAVE_REQUEST_CANCEL: 'LEAVE_REQUEST_CANCEL',

  LEAVE_BALANCE_VIEW: 'LEAVE_BALANCE_VIEW',
  LEAVE_BALANCE_MANAGE: 'LEAVE_BALANCE_MANAGE',

  HR_SETTINGS_VIEW: 'HR_SETTINGS_VIEW',
  HR_SETTINGS_MANAGE: 'HR_SETTINGS_MANAGE',

  // ---- salary -------------------------------------------------------------
  SALARY_COMPONENT_VIEW: 'SALARY_COMPONENT_VIEW',
  SALARY_COMPONENT_MANAGE: 'SALARY_COMPONENT_MANAGE',

  SALARY_STRUCTURE_VIEW: 'SALARY_STRUCTURE_VIEW',
  SALARY_STRUCTURE_MANAGE: 'SALARY_STRUCTURE_MANAGE',

  SALARY_ASSIGNMENT_VIEW: 'SALARY_ASSIGNMENT_VIEW',
  SALARY_ASSIGNMENT_MANAGE: 'SALARY_ASSIGNMENT_MANAGE',

  // ---- payroll ------------------------------------------------------------
  PAYROLL_PERIOD_VIEW: 'PAYROLL_PERIOD_VIEW',
  PAYROLL_PERIOD_MANAGE: 'PAYROLL_PERIOD_MANAGE',
  PAYROLL_CALCULATE: 'PAYROLL_CALCULATE',
  PAYROLL_APPROVE: 'PAYROLL_APPROVE',
  PAYROLL_PAY: 'PAYROLL_PAY',

  PAYSLIP_VIEW: 'PAYSLIP_VIEW',
  PAYSLIP_SELF_VIEW: 'PAYSLIP_SELF_VIEW',

  PAYROLL_ADJUSTMENT_VIEW: 'PAYROLL_ADJUSTMENT_VIEW',
  PAYROLL_ADJUSTMENT_MANAGE: 'PAYROLL_ADJUSTMENT_MANAGE',

  LOAN_VIEW: 'LOAN_VIEW',
  LOAN_MANAGE: 'LOAN_MANAGE',

  // ---- library ------------------------------------------------------------
  LIBRARY_VIEW: 'LIBRARY_VIEW',
  LIBRARY_MANAGE: 'LIBRARY_MANAGE',

  LIBRARY_LOCATION_VIEW: 'LIBRARY_LOCATION_VIEW',
  LIBRARY_LOCATION_MANAGE: 'LIBRARY_LOCATION_MANAGE',

  CATALOGUE_VIEW: 'CATALOGUE_VIEW',
  CATALOGUE_MANAGE: 'CATALOGUE_MANAGE',

  BOOK_COPY_VIEW: 'BOOK_COPY_VIEW',
  BOOK_COPY_MANAGE: 'BOOK_COPY_MANAGE',

  LIBRARY_MEMBER_VIEW: 'LIBRARY_MEMBER_VIEW',
  LIBRARY_MEMBER_MANAGE: 'LIBRARY_MEMBER_MANAGE',

  // ---- library: circulation -----------------------------------------------
  CIRCULATION_VIEW: 'CIRCULATION_VIEW',
  CIRCULATION_ISSUE: 'CIRCULATION_ISSUE',
  CIRCULATION_RETURN: 'CIRCULATION_RETURN',
  CIRCULATION_RENEW: 'CIRCULATION_RENEW',

  /** What a reader may do for themselves, without a librarian. */
  LIBRARY_SELF_BORROW: 'LIBRARY_SELF_BORROW',
  LIBRARY_SELF_VIEW: 'LIBRARY_SELF_VIEW',

  RESERVATION_VIEW: 'RESERVATION_VIEW',
  RESERVATION_MANAGE: 'RESERVATION_MANAGE',

  // ---- library: fines -----------------------------------------------------
  LIBRARY_FINE_VIEW: 'LIBRARY_FINE_VIEW',
  LIBRARY_FINE_MANAGE: 'LIBRARY_FINE_MANAGE',

  /**
   * Forgiving money owed. Deliberately not held by the librarian role: it is a
   * decision above the desk, the same split finance made between taking a
   * payment and approving a refund.
   */
  LIBRARY_FINE_WAIVE: 'LIBRARY_FINE_WAIVE',

  // ---- library: stock and buying ------------------------------------------
  LIBRARY_INVENTORY_VIEW: 'LIBRARY_INVENTORY_VIEW',
  LIBRARY_INVENTORY_MANAGE: 'LIBRARY_INVENTORY_MANAGE',

  ACQUISITION_VIEW: 'ACQUISITION_VIEW',
  ACQUISITION_MANAGE: 'ACQUISITION_MANAGE',

  LIBRARY_SUPPLIER_VIEW: 'LIBRARY_SUPPLIER_VIEW',
  LIBRARY_SUPPLIER_MANAGE: 'LIBRARY_SUPPLIER_MANAGE',

  LIBRARY_REPORT_VIEW: 'LIBRARY_REPORT_VIEW',

  // ---- transport ----------------------------------------------------------
  TRANSPORT_VIEW: 'TRANSPORT_VIEW',
  TRANSPORT_MANAGE: 'TRANSPORT_MANAGE',

  VEHICLE_VIEW: 'VEHICLE_VIEW',
  VEHICLE_MANAGE: 'VEHICLE_MANAGE',

  VEHICLE_DOCUMENT_VIEW: 'VEHICLE_DOCUMENT_VIEW',
  VEHICLE_DOCUMENT_MANAGE: 'VEHICLE_DOCUMENT_MANAGE',

  VEHICLE_MAINTENANCE_VIEW: 'VEHICLE_MAINTENANCE_VIEW',
  VEHICLE_MAINTENANCE_MANAGE: 'VEHICLE_MAINTENANCE_MANAGE',

  TRANSPORT_DRIVER_VIEW: 'TRANSPORT_DRIVER_VIEW',
  TRANSPORT_DRIVER_MANAGE: 'TRANSPORT_DRIVER_MANAGE',

  TRANSPORT_DRIVER_DOCUMENT_VIEW: 'TRANSPORT_DRIVER_DOCUMENT_VIEW',
  TRANSPORT_DRIVER_DOCUMENT_MANAGE: 'TRANSPORT_DRIVER_DOCUMENT_MANAGE',

  TRANSPORT_ROUTE_VIEW: 'TRANSPORT_ROUTE_VIEW',
  TRANSPORT_ROUTE_MANAGE: 'TRANSPORT_ROUTE_MANAGE',

  /**
   * Putting a vehicle or driver on a route. Split from TRANSPORT_ROUTE_MANAGE
   * so the coordinator can staff tomorrow's runs without being able to
   * redraw the route network.
   */
  TRANSPORT_ASSIGNMENT_MANAGE: 'TRANSPORT_ASSIGNMENT_MANAGE',

  TRANSPORT_ENROLLMENT_VIEW: 'TRANSPORT_ENROLLMENT_VIEW',
  TRANSPORT_ENROLLMENT_MANAGE: 'TRANSPORT_ENROLLMENT_MANAGE',

  TRANSPORT_TRIP_VIEW: 'TRANSPORT_TRIP_VIEW',
  TRANSPORT_TRIP_MANAGE: 'TRANSPORT_TRIP_MANAGE',

  TRANSPORT_ATTENDANCE_VIEW: 'TRANSPORT_ATTENDANCE_VIEW',
  TRANSPORT_ATTENDANCE_RECORD: 'TRANSPORT_ATTENDANCE_RECORD',

  TRANSPORT_INCIDENT_VIEW: 'TRANSPORT_INCIDENT_VIEW',
  TRANSPORT_INCIDENT_MANAGE: 'TRANSPORT_INCIDENT_MANAGE',

  TRANSPORT_TRACKING_VIEW: 'TRANSPORT_TRACKING_VIEW',
  TRANSPORT_TRACKING_RECORD: 'TRANSPORT_TRACKING_RECORD',

  TRANSPORT_REPORT_VIEW: 'TRANSPORT_REPORT_VIEW',

  /**
   * A driver's own world: their trips, their route, their roster. The
   * endpoints resolve the driver from the caller's account, so the permission
   * grants a view of oneself, not a view.
   */
  TRANSPORT_SELF_VIEW: 'TRANSPORT_SELF_VIEW',
  TRANSPORT_SELF_RECORD: 'TRANSPORT_SELF_RECORD',

  // ---- assets & stock -----------------------------------------------------
  ASSET_VIEW: 'ASSET_VIEW',
  /** Registering, editing and every named operation on an asset; reference data. */
  ASSET_MANAGE: 'ASSET_MANAGE',
  /** Opening, checking and closing counts. */
  ASSET_AUDIT: 'ASSET_AUDIT',
  STOCK_VIEW: 'STOCK_VIEW',
  /** Stock items and their movements. */
  STOCK_MANAGE: 'STOCK_MANAGE',

  // ---- notifications & announcements --------------------------------------
  /** The caller's own notification bell. */
  NOTIFICATION_SELF_VIEW: 'NOTIFICATION_SELF_VIEW',

  /** Reading the announcements addressed to one's own audience. */
  ANNOUNCEMENT_VIEW: 'ANNOUNCEMENT_VIEW',
  /** Drafting, publishing and archiving announcements. */
  ANNOUNCEMENT_MANAGE: 'ANNOUNCEMENT_MANAGE',

  // ---- portal self-service ------------------------------------------------
  /** A student's view of their own record, resolved from the linked account. */
  STUDENT_SELF_VIEW: 'STUDENT_SELF_VIEW',
  /** A parent's view of their linked children. */
  STUDENT_CHILD_VIEW: 'STUDENT_CHILD_VIEW',

  // ---- admissions ---------------------------------------------------------
  ADMISSION_VIEW: 'ADMISSION_VIEW',
  ADMISSION_MANAGE: 'ADMISSION_MANAGE',
  /**
   * Offering, rejecting and enrolling. Split from ADMISSION_MANAGE so the
   * office can process applications without deciding them.
   */
  ADMISSION_DECIDE: 'ADMISSION_DECIDE',

  // ---- platform -----------------------------------------------------------
  /**
   * Opens the spreadsheet import tool. Each type additionally needs the
   * target's own create permission, checked on the server.
   */
  IMPORT_MANAGE: 'IMPORT_MANAGE',
  /** The global search box; each record type still needs its own view permission. */
  SEARCH_USE: 'SEARCH_USE',
  /** The school dashboard summary; each section still needs its own permission. */
  DASHBOARD_VIEW: 'DASHBOARD_VIEW',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
